import { ApiClient } from 'lib/api/ApiClient.types';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import type { CalendarDate } from '@internationalized/date';
import { z } from 'zod';

const API_URL_V1 = import.meta.env.VITE_API_URL;
import type {
  AvailabilityRequest,
  AvailabilityRequestApi,
  CreateAvailabilityRequestEmailInput,
  CreateAvailabilityRequestLinkInput
} from './availability-request.api';

type AvailabilityRequestHttpBody = {
  resourceID: number;
  startTimestampUTC: string;
  endTimestampUTC: string;
  resourceEmail?: string;
  message?: string;
  enterpriseWorkspaceId?: number;
  enterpriseTeamId?: number;
};

function toAvailabilityRequestHttpBody(
  input:
    | CreateAvailabilityRequestEmailInput
    | CreateAvailabilityRequestLinkInput
): AvailabilityRequestHttpBody {
  const body: AvailabilityRequestHttpBody = {
    resourceID: input.resourceId,
    startTimestampUTC: input.dateRange.from.toDate('UTC').toISOString(),
    endTimestampUTC: input.dateRange.to.toDate('UTC').toISOString()
  };
  if ('resourceEmail' in input && input.resourceEmail) {
    body.resourceEmail = input.resourceEmail;
  }
  if ('message' in input && input.message) {
    body.message = input.message;
  }
  if (input.enterpriseWorkspaceId != null) {
    body.enterpriseWorkspaceId = input.enterpriseWorkspaceId;
  }
  if (input.enterpriseTeamId != null) {
    body.enterpriseTeamId = input.enterpriseTeamId;
  }
  return body;
}

const createdAvailabilityRequest$ = z.object({
  data: z.object({
    availabilityRequestID: z.number()
  })
});

const createdAvailabilityRequestWithLink$ = z.object({
  data: z.object({
    availabilityRequestID: z.number(),
    resourcePortalURL: z.string()
  })
});

/**
 * The v1 read contract (`GET /web/v1/workspaces/:schema/availability-requests`).
 * Field names are the database's own — the backend keeps them that way by rule
 * — so the joined contact arrives as its two columns and this module composes
 * the display name. Every field is nullable but `id`: a request whose resource
 * has been deleted, or one carrying no message, is still a request.
 */
const availabilityRequest$ = z.object({
  id: z.number(),
  resource_id: z.number().nullable(),
  resource_first_name: z.string().nullable(),
  resource_last_name: z.string().nullable(),
  requester_account_id: z.string().nullable(),
  message: z.string().nullable(),
  available_from: z.string().datetime().nullable(),
  available_to: z.string().datetime().nullable(),
  availability_requested: z.string().datetime().nullable(),
  availability_logged: z.string().datetime().nullable()
});

const availabilityRequestPage$ = z.object({
  data: z.array(availabilityRequest$),
  next_cursor: z.number().nullable()
});

const toDate = (value: string | null) => (value ? new Date(value) : null);

/** The last instant of a calendar day, for a backend filter that compares `<=`. */
const endOfDay = (day: CalendarDate) =>
  new Date(day.add({ days: 1 }).toDate('UTC').getTime() - 1).toISOString();

function availabilityRequestFromHttp(
  raw: z.infer<typeof availabilityRequest$>
): AvailabilityRequest {
  return {
    id: raw.id,
    resourceId: raw.resource_id,
    // The wire carries the DB's own columns; the display name is composed here.
    resourceName: [raw.resource_first_name, raw.resource_last_name]
      .filter(Boolean)
      .join(' '),
    requesterAccountId: raw.requester_account_id,
    message: raw.message,
    availableFrom: toDate(raw.available_from),
    availableTo: toDate(raw.available_to),
    requestedAt: toDate(raw.availability_requested),
    answeredAt: toDate(raw.availability_logged)
  };
}

export function createAvailabilityRequestApi(
  client: ApiClient
): AvailabilityRequestApi {
  return {
    async createAndSendEmail(input) {
      const response = await client.post(
        `/resource-portal/teams/:app_id:/availability-requests`,
        {
          data: toAvailabilityRequestHttpBody(input),
          token: true,
          isV2: true
        }
      );
      const parsed = parseWithDecoratedError(
        createdAvailabilityRequest$,
        response
      );
      return { availabilityRequestId: parsed.data.availabilityRequestID };
    },
    async createLink(input) {
      const response = await client.post(
        `/resource-portal/teams/:app_id:/availability-requests/link`,
        {
          data: toAvailabilityRequestHttpBody(input),
          token: true,
          isV2: true
        }
      );
      const parsed = parseWithDecoratedError(
        createdAvailabilityRequestWithLink$,
        response
      );
      return {
        availabilityRequestId: parsed.data.availabilityRequestID,
        resourcePortalUrl: parsed.data.resourcePortalURL
      };
    },
    /**
     * v1, not the v2 resource-portal routes the two creators use: the portal
     * serves one request to the resource who was asked, and this lists the
     * workspace's own.
     */
    async list(input, signal) {
      // The workspace is in the path rather than left to the client's implicit
      // fallback, the same way the production search builds its URL.
      const response = await client.get(
        `${API_URL_V1}/workspaces/${input.workspace}/availability-requests`,
        {
          token: true,
          signal,
          params: {
            ...(input.status ? { status: input.status } : {}),
            ...(input.dateRange
              ? {
                  from: input.dateRange.from.toDate('UTC').toISOString(),
                  // The window's last day is inclusive, so the filter runs to
                  // the end of it. Sending that day's midnight dropped every
                  // request that starts later the same day.
                  to: endOfDay(input.dateRange.to)
                }
              : {}),
            ...(input.resourceId ? { resource_id: input.resourceId } : {}),
            ...(input.after ? { after: input.after } : {}),
            ...(input.count ? { count: input.count } : {})
          }
        }
      );

      const parsed = parseWithDecoratedError(
        availabilityRequestPage$,
        response
      );
      return {
        items: parsed.data.map(availabilityRequestFromHttp),
        nextCursor: parsed.next_cursor
      };
    }
  };
}
